import type { SessionDto } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, ApiError, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { PasskeyCancelledError, passkeysSupported, signInWithPasskey } from '../lib/passkeys';
import { Icon } from '../components/Icon';

/**
 * Day / month / year pickers. The native date input follows the browser's locale (often mm/dd/yyyy),
 * so this keeps the familiar day-first order everywhere. Emits YYYY-MM-DD once all three are chosen.
 */
function BirthDateField({ value, onChange, invalid }: { value: string; onChange: (v: string) => void; invalid: boolean }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [initY, initM, initD] = value ? value.split('-').map(Number) : [0, 0, 0];
  const [parts, setParts] = useState({ d: initD || 0, m: initM || 0, y: initY || 0 });
  const thisYear = new Date().getFullYear();
  const daysIn = parts.y && parts.m ? new Date(parts.y, parts.m, 0).getDate() : 31;
  const months = Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(i18n.locale, { month: 'long' }).format(new Date(2000, i, 1)));
  const update = (k: 'd' | 'm' | 'y', v: number) => {
    const next = { ...parts, [k]: v };
    const max = next.y && next.m ? new Date(next.y, next.m, 0).getDate() : 31;
    if (next.d > max) next.d = max;
    setParts(next);
    const pad = (n: number) => String(n).padStart(2, '0');
    onChange(next.d && next.m && next.y ? `${next.y}-${pad(next.m)}-${pad(next.d)}` : '');
  };
  const select = (k: 'd' | 'm' | 'y', label: string, options: { v: number; label: string }[]) => (
    <select className="input" aria-label={label} aria-invalid={invalid} required value={parts[k] || ''} onChange={(e) => update(k, Number(e.target.value))}>
      <option value="" disabled>
        {label}
      </option>
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.label}
        </option>
      ))}
    </select>
  );
  return (
    <fieldset className="field birth-date" aria-describedby="birthDate-hint">
      <legend>{t('auth.birthDate')}</legend>
      <div className="birth-date-row">
        {select('d', t('auth.day'), Array.from({ length: daysIn }, (_, i) => ({ v: i + 1, label: String(i + 1) })))}
        {select('m', t('auth.month'), months.map((label, i) => ({ v: i + 1, label })))}
        {select('y', t('auth.year'), Array.from({ length: 110 }, (_, i) => ({ v: thisYear - i, label: String(thisYear - i) })))}
      </div>
      <span className="hint" id="birthDate-hint">
        {t('auth.birthDateHint')}
      </span>
    </fieldset>
  );
}

/** Sign-in pages: the brand and what the app offers on the left, a calm form on the right (form only on phones). */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { config, i18n } = useApp();
  const { t } = i18n;
  const name = config?.brand.name ?? 'DijitalApp';
  return (
    <main className="auth-split" id="main">
      <aside className="auth-hero" aria-hidden>
        <div className="auth-brand">
          <img src={config?.brand.icon ?? '/brand/icon.svg'} alt="" />
          <strong>{name}</strong>
        </div>
        <div className="auth-hero-body">
          <h2>{config?.brand.tagline[i18n.locale] ?? t('auth.hero.title')}</h2>
          <ul className="auth-points">
            {(
              [
                ['messages', 'auth.hero.p1'],
                ['users', 'auth.hero.p2'],
                ['learn', 'auth.hero.p3'],
              ] as const
            ).map(([icon, key]) => (
              <li key={key}>
                <span className="auth-point-icon">
                  <Icon name={icon} size={18} />
                </span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-hero-foot">{config?.brand.legal.copyright ?? `© ${name}`}</p>
      </aside>
      <section className="auth-panel">
        <div className="auth-card stack">
          <div className="auth-brand auth-brand-compact">
            <img src={config?.brand.icon ?? '/brand/icon.svg'} alt="" />
            <strong>{name}</strong>
          </div>
          <div className="auth-heading">
            <h1>{title}</h1>
            {subtitle && <p className="muted">{subtitle}</p>}
          </div>
          {children}
        </div>
      </section>
    </main>
  );
}

export function useSubmit() {
  const { i18n } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await fn();
    } catch (err) {
      setError(i18n.t(err instanceof PasskeyCancelledError ? 'auth.passkey.cancelled' : errorMessageKey(err)));
      if (err instanceof ApiError && err.fields) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };
  return { error, fields, busy, run };
}

export function LoginPage() {
  const { i18n, config, setMe } = useApp();
  const { t } = i18n;
  const navigate = useNavigate();
  const { error, busy, run } = useSubmit();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [passkeyReady, setPasskeyReady] = useState(false);

  useEffect(() => {
    void passkeysSupported().then(setPasskeyReady, () => setPasskeyReady(false));
  }, []);

  const location = useLocation();
  const enter = (session: SessionDto) => {
    setMe(session.me);
    // Only an in-app group invite path is honoured as a return target.
    const from = (location.state as { from?: unknown } | null)?.from;
    const back = typeof from === 'string' && from.startsWith('/join#') ? from : null;
    navigate(!session.me.onboardingCompleted ? '/onboarding' : (back ?? '/'), { replace: true });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => enter(await api<SessionDto>('POST', '/auth/login', { identifier, password })));
  };

  const onPasskey = () => void run(async () => enter(await signInWithPasskey()));

  return (
    <AuthLayout title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
        <div className="field">
          <label htmlFor="identifier">{t('auth.identifier')}</label>
          <input id="identifier" className="input" autoComplete="username" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">{t('auth.password')}</label>
          <input id="password" className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-block" disabled={busy}>
          {t('auth.submitLogin')}
        </button>
        <p className="auth-link-row">
          <Link to="/forgot-password">{t('auth.forgotLink')}</Link>
        </p>
        {config?.authMethods.passkey && passkeyReady && (
          <>
            <div className="auth-divider">
              <span>{t('auth.or')}</span>
            </div>
            <button className="btn btn-block" type="button" disabled={busy} onClick={onPasskey}>
              <Icon name="key" size={18} /> {t('auth.passkey.login')}
            </button>
          </>
        )}
        <p className="auth-switch">
          {t('auth.noAccount')} <Link to="/register">{t('auth.register.title')}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

const COUNTRIES = ['TR', 'DE', 'US', 'GB', 'NL', 'FR', 'AZ'];

export function RegisterPage() {
  const { i18n, config, setMe } = useApp();
  const { t } = i18n;
  const navigate = useNavigate();
  const { error, fields, busy, run } = useSubmit();
  const [form, setForm] = useState({
    displayName: '',
    username: '',
    email: '',
    password: '',
    birthDate: '',
    country: 'TR',
    accept: false,
    // Invite links look like /register#invite=ABCD-EFGH-JKMN (fragment: never sent to servers).
    inviteCode: new URLSearchParams(window.location.hash.slice(1)).get('invite') ?? '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string; checked?: boolean; type?: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!form.accept) throw new ApiError('TERMS_NOT_ACCEPTED', 400);
      const session = await api<SessionDto>('POST', '/auth/register', {
        displayName: form.displayName,
        username: form.username,
        email: form.email,
        password: form.password,
        birthDate: form.birthDate,
        country: form.country,
        locale: i18n.locale,
        acceptTermsVersion: config?.termsVersion,
        ...(config?.registrationMode === 'invite' ? { inviteCode: form.inviteCode } : {}),
      });
      setMe(session.me);
      navigate('/onboarding', { replace: true });
    });
  };

  const field = (id: keyof typeof form, label: string, props: Record<string, string> = {}, hint?: string) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        value={String(form[id])}
        onChange={set(id)}
        aria-invalid={id in fields}
        aria-describedby={hint ? `${id}-hint` : undefined}
        required
        {...props}
      />
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );

  return (
    <AuthLayout title={t('auth.register.title')} subtitle={t('auth.register.subtitle')}>
      {config?.registrationMode === 'closed' ? (
        <div className="stack">
          <p className="alert alert-info" role="status">
            {t('auth.registrationClosed')}
          </p>
          <Link className="btn btn-primary btn-block" to="/login">
            {t('auth.login.title')}
          </Link>
        </div>
      ) : (
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
        {config?.registrationMode === 'invite' &&
          field('inviteCode', t('auth.inviteCode'), { autoComplete: 'off', autoCapitalize: 'characters' }, t('auth.inviteHint'))}
        {field('displayName', t('auth.displayName'), { autoComplete: 'name' })}
        {field('username', t('auth.username'), { autoComplete: 'username', autoCapitalize: 'none' })}
        {field('email', t('auth.email'), { type: 'email', autoComplete: 'email' })}
        {field('password', t('auth.password'), { type: 'password', autoComplete: 'new-password', minLength: '10' }, t('auth.passwordHint'))}
        <BirthDateField
          value={form.birthDate}
          onChange={(birthDate) => setForm((f) => ({ ...f, birthDate }))}
          invalid={'birthDate' in fields}
        />

        <div className="field">
          <label htmlFor="country">{t('auth.country')}</label>
          <select id="country" className="input" value={form.country} onChange={set('country')}>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {new Intl.DisplayNames([i18n.locale], { type: 'region' }).of(c)}
              </option>
            ))}
          </select>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.accept} onChange={set('accept')} />
          <span>
            {t('auth.acceptTerms')}{' '}
            <a href={config?.brand.legal.termsUrl} target="_blank" rel="noreferrer">
              ↗
            </a>
          </span>
        </label>
        <button className="btn btn-primary btn-block" disabled={busy}>
          {t('auth.submitRegister')}
        </button>
        <p className="muted">
          {t('auth.haveAccount')} <Link to="/login">{t('auth.login.title')}</Link>
        </p>
      </form>
      )}
    </AuthLayout>
  );
}
